/**
 * Microsoft Graph API Service
 * ---------------------------
 * Handles all calendar operations: reading availability, creating bookings,
 * and cancelling existing reservations.
 *
 * Two modes of operation:
 * 1. User-authenticated (web/phone): Uses the user's delegated token via MSAL
 * 2. API-proxied (kiosk): Calls the Azure Functions API, which uses
 *    app-only credentials to read calendars.
 */
import { AuthService } from './authService';
import { getOfficeConfig } from '../config/offices';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Make an authenticated request to Microsoft Graph.
 */
async function graphFetch(endpoint, options = {}) {
  const token = await AuthService.getToken();
  const res = await fetch(`${GRAPH_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Graph API error: ${res.status}`);
  }
  return res.json();
}

/**
 * Make a request to our Azure Functions API (used by kiosk).
 */
async function apiFetch(endpoint, options = {}) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `API error: ${res.status}`);
  }
  return res.json();
}

export const GraphService = {
  /**
   * Get availability for all rooms (user-authenticated mode).
   * Uses the Graph schedules endpoint to batch-check free/busy status.
   */
  async getAvailability(date) {
    const config = getOfficeConfig();
    const roomEmails = config.rooms.filter(r => r.email).map(r => r.email);

    const today = new Date().toISOString().split('T')[0];
    const isToday = !date || date === today;

    // For today, start from now; for future dates, start from beginning of day
    const startTime = isToday
      ? new Date()
      : new Date(date + 'T00:00:00');
    const endTime = new Date((date || today) + 'T23:59:59');

    // Graph API: getSchedule returns free/busy for multiple users.
    // Use UTC timezone to match toISOString() output and avoid offset mismatches.
    const response = await graphFetch('/me/calendar/getSchedule', {
      method: 'POST',
      body: JSON.stringify({
        schedules: roomEmails,
        startTime: {
          dateTime: startTime.toISOString(),
          timeZone: 'UTC',
        },
        endTime: {
          dateTime: endTime.toISOString(),
          timeZone: 'UTC',
        },
        availabilityViewInterval: 30,
      }),
    });

    return parseScheduleResponse(response, config, isToday);
  },

  /**
   * Get availability via the API (kiosk mode, app-only auth).
   */
  async getAvailabilityViaApi(date) {
    const config = getOfficeConfig();
    const dateParam = date ? `&date=${date}` : '';
    const response = await apiFetch(`/availability?office=${config.id}${dateParam}`);
    return response;
  },

  /**
   * Create a booking: creates an event on the room calendar and
   * sends a calendar invite to the requestor.
   */
  async createBooking(bookingDetails, user) {
    const config = getOfficeConfig();
    const room = config.rooms.find(r => r.id === bookingDetails.roomId);

    if (!room) throw new Error(`Room ${bookingDetails.roomId} not found in config.`);

    const startDateTime = `${bookingDetails.date}T${bookingDetails.startTime}:00`;
    const endDateTime = `${bookingDetails.date}T${bookingDetails.endTime}:00`;

    // Create the calendar event with the room as an attendee (resource).
    // This automatically books the room calendar and sends invites.
    const event = {
      subject: bookingDetails.subject || `${room.name} Reservation`,
      body: {
        contentType: 'HTML',
        content: `<p>Room booking for <strong>${room.name}</strong> at the ${config.name} office.</p>
                  <p>Booked by: ${user.name} (${user.username})</p>`,
      },
      start: {
        dateTime: startDateTime,
        timeZone: 'Central Standard Time',
      },
      end: {
        dateTime: endDateTime,
        timeZone: 'Central Standard Time',
      },
      location: {
        displayName: `${room.name} - ${config.fullName}`,
        locationEmailAddress: room.email,
        locationType: 'conferenceRoom',
        // Postal address comes from the office config so each office
        // gets the correct street address on calendar events.
        address: config.postalAddress,
      },
      attendees: [
        {
          emailAddress: {
            address: room.email,
            name: room.name,
          },
          type: 'resource',
        },
      ],
      // Always 'busy' so the room calendar properly blocks the time slot.
      // Previously office/cubicle used 'free' to keep the employee's calendar open,
      // but that caused rooms to not show as booked and allowed double-booking.
      showAs: 'busy',
      isReminderOn: true,
      reminderMinutesBeforeStart: 15,
    };

    // Add weekly recurrence pattern for recurring cubicle bookings
    if (bookingDetails.recurrence) {
      event.recurrence = {
        pattern: {
          type: 'weekly',
          interval: 1,
          daysOfWeek: bookingDetails.recurrence.daysOfWeek,
        },
        range: {
          type: 'endDate',
          startDate: bookingDetails.recurrence.startDate,
          endDate: bookingDetails.recurrence.endDate,
        },
      };
    }

    const result = await graphFetch('/me/events', {
      method: 'POST',
      body: JSON.stringify(event),
    });

    // NOTE: We previously PATCHed office/cubicle events to showAs:'free' on the
    // employee's calendar, but Exchange may propagate that change to the room
    // calendar as a meeting update, causing the room to appear available.
    // Keeping showAs:'busy' for all bookings ensures rooms always show as booked.
    // The trade-off is the employee shows as busy for desk bookings.

    return result;
  },

  /**
   * Cancel an existing booking by event ID.
   * Used when enforcing the one-booking-per-employee rule.
   */
  async cancelBooking(eventId) {
    await graphFetch(`/me/events/${eventId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Check if a user already has an office or cubicle booked for today.
   * Returns the existing booking details or null.
   */
  async getUserExistingBooking(userEmail) {
    const config = getOfficeConfig();
    const officeAndCubicleEmails = config.rooms
      .filter(r => (r.type === 'office' || r.type === 'cubicle') && r.email)
      .map(r => r.email);

    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);

    try {
      // Get today's events for the current user
      const response = await graphFetch(
        `/me/calendarView?startDateTime=${now.toISOString()}&endDateTime=${endOfDay.toISOString()}&$select=id,subject,start,end,location,attendees,isOrganizer`
      );

      // Find any event that has an office/cubicle room as an attendee
      for (const event of response.value || []) {
        // Only count rooms the user actually booked. Meetings they were merely
        // invited to also carry the room as a resource attendee, but the user
        // is not the organizer, so skip those to avoid false conflicts.
        if (!event.isOrganizer) continue;
        const roomAttendee = event.attendees?.find(a =>
          officeAndCubicleEmails.includes(a.emailAddress?.address?.toLowerCase())
        );
        if (roomAttendee) {
          const room = config.rooms.find(r =>
            r.email?.toLowerCase() === roomAttendee.emailAddress.address.toLowerCase()
          );
          return {
            eventId: event.id,
            roomId: room?.id,
            roomName: room?.name || roomAttendee.emailAddress.name,
            start: event.start.dateTime,
            end: event.end.dateTime,
          };
        }
      }
    } catch (err) {
      console.error('Failed to check existing bookings:', err);
    }
    return null;
  },

  /**
   * Get all active (current + future) room bookings for the user.
   * Used to enforce the max-2-active-bookings rule.
   * Looks 30 days ahead to cover the 4-week advance booking window.
   */
  async getUserActiveBookings() {
    const config = getOfficeConfig();
    const roomEmails = config.rooms
      .filter(r => r.email)
      .map(r => r.email.toLowerCase());

    const now = new Date();
    const futureLimit = new Date();
    futureLimit.setDate(futureLimit.getDate() + 30);

    try {
      const response = await graphFetch(
        `/me/calendarView?startDateTime=${now.toISOString()}&endDateTime=${futureLimit.toISOString()}&$select=id,subject,start,end,attendees,isOrganizer&$top=50`
      );

      const bookings = [];
      for (const event of response.value || []) {
        // Only count rooms the user actually booked, not meetings they were
        // invited to (those carry the room as a resource attendee but the
        // user is not the organizer).
        if (!event.isOrganizer) continue;
        const roomAttendee = event.attendees?.find(a =>
          roomEmails.includes(a.emailAddress?.address?.toLowerCase())
        );
        if (roomAttendee) {
          const room = config.rooms.find(r =>
            r.email?.toLowerCase() === roomAttendee.emailAddress.address.toLowerCase()
          );
          bookings.push({
            eventId: event.id,
            roomId: room?.id,
            roomName: room?.name || roomAttendee.emailAddress.name,
            roomType: room?.type,
            start: event.start.dateTime,
            end: event.end.dateTime,
          });
        }
      }
      return bookings;
    } catch (err) {
      console.error('Failed to check active bookings:', err);
      return [];
    }
  },
};

/**
 * Parse the Graph getSchedule response into our availability format.
 */
function parseScheduleResponse(response, config, isToday = true) {
  const availability = {};

  for (const schedule of response.value || []) {
    const room = config.rooms.find(r =>
      r.email.toLowerCase() === schedule.scheduleId.toLowerCase()
    );
    if (!room) continue;

    const now = new Date();
    let currentlyBooked = false;
    let bookedBy = null;
    const slots = [];

    for (const item of schedule.scheduleItems || []) {
      // Response datetimes are in UTC (matching our request timezone).
      // Append 'Z' if missing so JavaScript parses them correctly.
      const startStr = item.start.dateTime.endsWith('Z') ? item.start.dateTime : item.start.dateTime + 'Z';
      const endStr = item.end.dateTime.endsWith('Z') ? item.end.dateTime : item.end.dateTime + 'Z';
      const start = new Date(startStr);
      const end = new Date(endStr);

      // Only mark "currently booked" for today's view
      if (isToday && now >= start && now < end) {
        currentlyBooked = true;
        bookedBy = 'Reserved';
      }

      slots.push({
        start: item.start.dateTime,
        end: item.end.dateTime,
        status: item.status,
      });
    }

    // For future dates, "booked" means has any slots at all
    if (!isToday && slots.length > 0) {
      currentlyBooked = false; // Not "currently" booked, but has bookings
    }

    availability[room.id] = { currentlyBooked, bookedBy, slots };
  }

  // Mark rooms with no schedule data as available
  for (const room of config.rooms) {
    if (!availability[room.id]) {
      availability[room.id] = { currentlyBooked: false, bookedBy: null, slots: [] };
    }
  }

  return availability;
}
